import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Brain, Cpu, Database, Zap, Shield, TrendingUp, Activity, Layers } from 'lucide-react';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';

const AITechnology = () => {
  // Structured Data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "AI Technology - How PickStar's Sports Betting AI Works",
    "description": "Discover the cutting-edge AI technology behind PickStar's sports betting predictions. Machine learning, neural networks, and real-time data analysis explained.",
    "author": {
      "@type": "Organization",
      "name": "PickStar AI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStar AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-08-22",
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/resources/ai-technology"
    },
    "image": "https://pickstarai.com/og-ai-technology.jpg"
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How does PickStar AI predict sports outcomes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "PickStar AI uses neural networks, big data processing, predictive analytics, and NLP to analyze millions of data points including historical games, live odds, player performance, and real-time conditions to generate predictions with elite accuracy."
        }
      },
      {
        "@type": "Question",
        "name": "What makes PickStar AI different from other betting systems?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "PickStar AI combines multiple AI technologies, processes 2.8M+ games, provides predictions in 0.3 seconds, and continuously learns from outcomes. It offers unbiased analysis, real-time processing, and transparent reasoning for every prediction."
        }
      },
      {
        "@type": "Question",
        "name": "How accurate is PickStar's AI technology?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "PickStar AI has been backtested on over 10 years of historical data with elite performance metrics. The system continuously improves through machine learning and has analyzed over 2.8 million games with industry-leading success rates."
        }
      }
    ]
  };

  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "PickStar AI Sports Betting System",
    "applicationCategory": "Sports Betting AI",
    "operatingSystem": "Web-based",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "10000"
    },
    "description": "Advanced AI system for sports betting predictions using neural networks and machine learning"
  };

  return (
    <>
      <SEOHead 
        title="AI Technology - How Our Sports Betting AI Works | PickStar AI"
        description="Discover the cutting-edge AI technology powering PickStar's elite sports predictions. Neural networks, machine learning, big data analytics, and real-time processing explained."
        keywords="sports betting AI, machine learning betting, neural networks sports, predictive analytics, big data sports betting, AI predictions, betting technology, sports AI system"
        canonical="https://pickstarai.com/resources/ai-technology"
        ogImage="https://pickstarai.com/og-ai-technology.jpg"
        structuredData={[articleStructuredData, faqStructuredData, organizationStructuredData]}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        {/* Header */}
        <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <nav className="max-w-4xl mx-auto px-4 py-4">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors"
              aria-label="Return to PickStar AI homepage"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Home
            </Link>
          </nav>
        </header>

        {/* Hero Section */}
        <section className="py-8 sm:py-12 md:py-16 px-4" aria-labelledby="hero-heading">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Brain className="w-8 h-8 text-emerald-400" aria-hidden="true" />
              <h1 id="hero-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
                AI Technology
              </h1>
            </div>
            <p className="text-lg sm:text-xl text-gray-300 mb-8">
              The cutting-edge science powering industry-leading sports predictions
            </p>
          </div>
        </section>

        {/* Main Content */}
        <main className="px-4 pb-16">
          <div className="max-w-4xl mx-auto space-y-12">
            
            {/* Introduction */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="intro-heading">
              <h2 id="intro-heading" className="text-xl sm:text-2xl font-bold text-white mb-4">
                Next-Generation Sports Intelligence
              </h2>
              <p className="text-gray-300 mb-4">
                PickStar AI represents a breakthrough in sports betting technology. Our proprietary system 
                combines multiple AI technologies to deliver predictions that consistently outperform traditional 
                betting methods and human experts.
              </p>
              <p className="text-gray-300">
                Built on years of research and millions of historical games, our AI doesn't just predict 
                outcomes—it understands the complex dynamics that determine sporting results.
              </p>
            </section>

            {/* Core Technologies */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="tech-heading">
              <div className="flex items-center gap-3 mb-6">
                <Cpu className="w-6 h-6 text-emerald-400" aria-hidden="true" />
                <h2 id="tech-heading" className="text-xl sm:text-2xl font-bold text-white">Core Technologies</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <article className="bg-black/30 rounded-lg p-4 sm:p-6 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-5 h-5 text-emerald-400" aria-hidden="true" />
                    <h3 className="text-lg font-semibold text-white">Neural Networks</h3>
                  </div>
                  <p className="text-gray-300 text-sm mb-3">
                    Deep learning models with millions of parameters trained on historical sports data
                  </p>
                  <ul className="text-gray-400 text-xs space-y-1">
                    <li>• Transformer architectures for sequence prediction</li>
                    <li>• Convolutional networks for pattern recognition</li>
                    <li>• Recurrent networks for time-series analysis</li>
                  </ul>
                </article>

                <article className="bg-black/30 rounded-lg p-4 sm:p-6 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Database className="w-5 h-5 text-emerald-400" aria-hidden="true" />
                    <h3 className="text-lg font-semibold text-white">Big Data Processing</h3>
                  </div>
                  <p className="text-gray-300 text-sm mb-3">
                    Real-time analysis of millions of data points per second
                  </p>
                  <ul className="text-gray-400 text-xs space-y-1">
                    <li>• Historical game data from 20+ years</li>
                    <li>• Live odds from 50+ sportsbooks</li>
                    <li>• Social media sentiment analysis</li>
                  </ul>
                </article>

                <article className="bg-black/30 rounded-lg p-4 sm:p-6 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Activity className="w-5 h-5 text-emerald-400" aria-hidden="true" />
                    <h3 className="text-lg font-semibold text-white">Predictive Analytics</h3>
                  </div>
                  <p className="text-gray-300 text-sm mb-3">
                    Advanced statistical models for outcome probability
                  </p>
                  <ul className="text-gray-400 text-xs space-y-1">
                    <li>• Bayesian inference for uncertainty</li>
                    <li>• Monte Carlo simulations</li>
                    <li>• Ensemble methods for accuracy</li>
                  </ul>
                </article>

                <article className="bg-black/30 rounded-lg p-4 sm:p-6 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Layers className="w-5 h-5 text-emerald-400" aria-hidden="true" />
                    <h3 className="text-lg font-semibold text-white">NLP Processing</h3>
                  </div>
                  <p className="text-gray-300 text-sm mb-3">
                    Understanding context from news and reports
                  </p>
                  <ul className="text-gray-400 text-xs space-y-1">
                    <li>• Injury report analysis</li>
                    <li>• Team news extraction</li>
                    <li>• Coach statement interpretation</li>
                  </ul>
                </article>
              </div>
            </section>

            {/* How It Works */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="how-heading">
              <div className="flex items-center gap-3 mb-6">
                <Zap className="w-6 h-6 text-emerald-400" aria-hidden="true" />
                <h2 id="how-heading" className="text-xl sm:text-2xl font-bold text-white">How Our AI Works</h2>
              </div>
              
              <div className="space-y-6">
                <div className="relative">
                  <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-emerald-500/30 hidden sm:block"></div>
                  
                  <ol className="space-y-6 sm:space-y-8">
                    <li className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">1</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">Data Collection</h3>
                        <p className="text-gray-300 text-sm">
                          Our system continuously collects data from multiple sources: game statistics, 
                          player performance metrics, weather conditions, injury reports, betting odds movements, 
                          and historical matchup data.
                        </p>
                      </div>
                    </li>
                    
                    <li className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">2</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">Feature Engineering</h3>
                        <p className="text-gray-300 text-sm">
                          Raw data is transformed into meaningful features: team form indexes, 
                          head-to-head performance, situational statistics, and momentum indicators 
                          that our models can process effectively.
                        </p>
                      </div>
                    </li>
                    
                    <li className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">3</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">Model Prediction</h3>
                        <p className="text-gray-300 text-sm">
                          Multiple specialized AI models analyze the data simultaneously. Each model 
                          focuses on different aspects: scoring patterns, defensive metrics, special teams, 
                          and situational advantages.
                        </p>
                      </div>
                    </li>
                    
                    <li className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">4</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">Ensemble Consensus</h3>
                        <p className="text-gray-300 text-sm">
                          Our ensemble system combines predictions from all models, weighing them based 
                          on historical accuracy and current confidence levels to produce a final prediction 
                          with probability scores.
                        </p>
                      </div>
                    </li>
                    
                    <li className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">5</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">Human-Readable Insights</h3>
                        <p className="text-gray-300 text-sm">
                          Our natural language generation system translates complex predictions into 
                          clear, actionable insights explaining the reasoning behind each pick.
                        </p>
                      </div>
                    </li>
                  </ol>
                </div>
              </div>
            </section>

            {/* Performance Metrics */}
            <section className="bg-gradient-to-r from-emerald-500/10 to-violet-500/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-emerald-500/20" aria-labelledby="metrics-heading">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="w-6 h-6 text-emerald-400" aria-hidden="true" />
                <h2 id="metrics-heading" className="text-xl sm:text-2xl font-bold text-white">Performance Metrics</h2>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <div className="bg-black/30 rounded-lg p-3 sm:p-4 text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-400 mb-1">Elite</div>
                  <div className="text-xs text-gray-400">Performance</div>
                </div>
                <div className="bg-black/30 rounded-lg p-3 sm:p-4 text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-400 mb-1">2.8M</div>
                  <div className="text-xs text-gray-400">Games Analyzed</div>
                </div>
                <div className="bg-black/30 rounded-lg p-3 sm:p-4 text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-400 mb-1">0.3s</div>
                  <div className="text-xs text-gray-400">Avg Response</div>
                </div>
                <div className="bg-black/30 rounded-lg p-3 sm:p-4 text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-400 mb-1">24/7</div>
                  <div className="text-xs text-gray-400">Availability</div>
                </div>
              </div>
              
              <p className="text-gray-300 text-sm">
                Our AI system has been backtested on over 10 years of historical data and continuously 
                improves through machine learning. Performance is independently verified and updated daily.
              </p>
            </section>

            {/* Continuous Learning */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="learning-heading">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-6 h-6 text-emerald-400" aria-hidden="true" />
                <h2 id="learning-heading" className="text-xl sm:text-2xl font-bold text-white">
                  Continuous Learning & Improvement
                </h2>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-300">
                  Our AI doesn't just make predictions—it learns from every outcome to become more accurate over time:
                </p>
                
                <div className="space-y-3">
                  <article className="bg-black/30 rounded-lg p-4 border-l-4 border-emerald-500">
                    <h3 className="font-semibold text-white mb-1">Daily Model Updates</h3>
                    <p className="text-gray-300 text-sm">
                      Models are retrained nightly with the latest game results and adjusted for emerging patterns
                    </p>
                  </article>
                  
                  <article className="bg-black/30 rounded-lg p-4 border-l-4 border-violet-500">
                    <h3 className="font-semibold text-white mb-1">Adaptive Algorithms</h3>
                    <p className="text-gray-300 text-sm">
                      Our system automatically adjusts to rule changes, roster updates, and meta shifts in sports
                    </p>
                  </article>
                  
                  <article className="bg-black/30 rounded-lg p-4 border-l-4 border-emerald-500">
                    <h3 className="font-semibold text-white mb-1">Error Analysis</h3>
                    <p className="text-gray-300 text-sm">
                      Every incorrect prediction is analyzed to identify patterns and improve future accuracy
                    </p>
                  </article>
                </div>
              </div>
            </section>

            {/* Why Choose Our AI */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="why-heading">
              <h2 id="why-heading" className="text-xl sm:text-2xl font-bold text-white mb-6">
                Why PickStar AI Leads the Industry
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 mt-2" aria-hidden="true"></div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Unbiased Analysis</h3>
                      <p className="text-gray-300 text-sm">
                        No emotional attachment or human bias—pure data-driven predictions
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 mt-2" aria-hidden="true"></div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Real-Time Processing</h3>
                      <p className="text-gray-300 text-sm">
                        Instant analysis of breaking news and lineup changes
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 mt-2" aria-hidden="true"></div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Comprehensive Coverage</h3>
                      <p className="text-gray-300 text-sm">
                        Analysis for all major sports and leagues worldwide
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-violet-400 mt-2" aria-hidden="true"></div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Transparent Reasoning</h3>
                      <p className="text-gray-300 text-sm">
                        Clear explanations for every prediction and confidence level
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-violet-400 mt-2" aria-hidden="true"></div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Proven Track Record</h3>
                      <p className="text-gray-300 text-sm">
                        Industry-leading success rate verified across millions of predictions
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-violet-400 mt-2" aria-hidden="true"></div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Constant Evolution</h3>
                      <p className="text-gray-300 text-sm">
                        AI that learns and improves with every game
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="text-center py-8" aria-labelledby="cta-heading">
              <h2 id="cta-heading" className="text-xl sm:text-2xl font-bold text-white mb-4">
                Experience the Future of Sports Betting
              </h2>
              <p className="text-gray-300 mb-6">
                See our AI technology in action with your first prediction
              </p>
              <Link 
                to="/chat"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-violet-500 text-white px-6 sm:px-8 py-3 rounded-lg font-semibold hover:from-emerald-600 hover:to-violet-600 transition-all"
                aria-label="Try AI predictions now"
              >
                Try AI Predictions Now
              </Link>
            </section>
          </div>
        </main>

        {/* Footer */}
        <SEOFooter language="en" />
      </div>

      {/* Structured Data Scripts */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationStructuredData) }}
      />
    </>
  );
};

export default AITechnology;